import {
  Autocomplete,
  TextField,
  FormHelperText,
  Typography,
  Box,
  Checkbox,
} from "@mui/material";
import { Controller } from "react-hook-form";
import { useRef, useState, useEffect } from "react";
import { FixedSizeList } from "react-window";
import { AutocompleteTextStyle } from "../routes/HeaderMenu/style/style";
import { debounce, isEmpty } from "lodash";
import fontWeight from "../styles/mui/fontWeight";
import colors from "../styles/colors/colors";
import { getHeight } from "../utils";
import { checkboxStyle, labelTypography } from "./styles/styles";

export interface ComponentTypes {
  control: any;
  name: string;
  multiple: boolean;
  helper?: any;
  style?: any;
  list?: any;
  mandatorySymbol?: boolean;
  disabled?: boolean;
  sx?: any;
  onChangeHandler?: any;
}

export const FormAutocompleteSelect = ({
  control,
  name,
  multiple,
  list,
  disabled,
  helper,
  sx,
  onChangeHandler,
}: ComponentTypes) => {
  const [inputValue, setInputValue] = useState<string>("");
  const [filteredOptions, setFilteredOptions] = useState(list);
  const [highlightedIndex, setHighlightedIndex] = useState<number | null>(null);
  const listRef = useRef<any>(null);
  const [open, setOpen] = useState(false);

  const fetchFilteredOptions = debounce((query: string) => {
    const lowerQuery = query.toLowerCase().trim();
    const filtered =
      list &&
      list.filter((item: any) => item.label.toLowerCase().includes(lowerQuery));
    !isEmpty(filtered) && setFilteredOptions(filtered);
  }, 500);

  const handleInputChange = (_: any, value: string, reason: string) => {
    if (reason === "reset") return;
    setInputValue(value);
    if (value.trim() === "") {
      setFilteredOptions(list);
    } else {
      fetchFilteredOptions(value);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent, field: any) => {
    let newIndex = highlightedIndex ?? 0;
    const totalOptions = filteredOptions.length;
    if (e.key === "ArrowDown") {
      newIndex = Math.min(totalOptions - 1, newIndex + 1);
      setHighlightedIndex(newIndex);
    } else if (e.key === "ArrowUp") {
      newIndex = Math.max(0, newIndex - 1);
      setHighlightedIndex(newIndex);
    } else if (e.key === "Enter" && highlightedIndex !== null) {
      const selectedItem = filteredOptions[highlightedIndex];
      let newValue = [...(field.value || [])];
      if (multiple) {
        const indexOfItem = newValue.findIndex(
          (item: any) => item.value === selectedItem.value
        );
        if (indexOfItem === -1) {
          newValue.push(selectedItem);
        } else {
          newValue =
            newValue &&
            newValue.filter((item: any) => item.value !== selectedItem.value);
        }
      } else {
        newValue = [selectedItem];
      }
      !isEmpty(newValue) && field.onChange(newValue);
      onChangeHandler && onChangeHandler(e, newValue);
      !isEmpty(list) && setFilteredOptions(list);
      setInputValue("");
    }
  };

  const handleBlur = () => {
    setFilteredOptions(list);
    if (open) {
      setOpen(false);
    }
  };

  useEffect(() => {
    if (inputValue.trim() === "") {
      setFilteredOptions(list);
    } else {
      fetchFilteredOptions(inputValue);
    }
  }, [list]);

  return (
    <Controller
      name={name}
      control={control}
      rules={helper}
      render={({ field, fieldState: { error } }) => (
        <>
          <Autocomplete
            {...field}
            onInputChange={handleInputChange}
            disabled={disabled ? disabled : false}
            multiple={multiple}
            options={filteredOptions}
            openOnFocus
            disableClearable
            disableCloseOnSelect
            onChange={(e, value) => {
              field.onChange(value);
              onChangeHandler?.(e, value);
            }}
            value={field.value || []}
            getOptionLabel={(option) => option.label}
            inputValue={inputValue}
            sx={sx}
            renderInput={(params) => (
              <TextField
                {...params}
                sx={AutocompleteTextStyle}
                variant="standard"
                error={!!error?.message}
                onBlur={handleBlur}
                onKeyDown={(e) => handleKeyDown(e, field)}
                value={inputValue}
              />
            )}
            renderOption={(_props, option) => (
              <Typography
                style={{ cursor: "pointer" }}
                variant="body1"
                fontWeight={fontWeight[4]}
                color={colors.black[4]}
              >
                {option.label}
              </Typography>
            )}
            ListboxComponent={() => (
              <FixedSizeList
                ref={listRef}
                height={getHeight(filteredOptions.length)}
                itemSize={36}
                itemCount={filteredOptions.length}
                width="100%"
                itemData={filteredOptions}
              >
                {({ index, style, data }: any) => {
                  const option = data[index];
                  const isSelected = (field.value || []).some(
                    (item: any) => item.value === option.value
                  );

                  return (
                    <Box
                      key={index}
                      style={{
                        ...style,
                        display: "flex",
                        alignItems: "center",
                        padding: "0 12px",
                        cursor: "pointer",
                        backgroundColor:
                          highlightedIndex === index
                            ? "lightgray"
                            : "transparent",
                      }}
                      onMouseDown={(e: any) => {
                        e.preventDefault();
                        const scrollOffset =
                          listRef.current?.state?.scrollOffset;
                        const selectedItem = data[index];
                        let newValue = [...(field.value || [])];
                        const indexOfItem = newValue.findIndex(
                          (item) => item.value === selectedItem.value
                        );
                        if (indexOfItem === -1) {
                          newValue.push(selectedItem);
                        } else {
                          newValue =
                            newValue &&
                            newValue.filter(
                              (item) => item.value !== selectedItem.value
                            );
                        }

                        field.onChange(newValue);
                        onChangeHandler?.(e, newValue);

                        setTimeout(() => {
                          if (listRef.current) {
                            listRef.current.scrollTo(scrollOffset);
                          }
                        }, 0);
                      }}
                    >
                      <Checkbox
                        size="small"
                        checked={isSelected}
                        sx={checkboxStyle}
                      />
                      <Typography
                        sx={labelTypography}
                        variant="body1"
                        fontWeight={fontWeight[4]}
                        color={colors.black[4]}
                      >
                        {data[index].label}
                      </Typography>
                    </Box>
                  );
                }}
              </FixedSizeList>
            )}
          />
          {error?.message && (
            <FormHelperText error>{error.message}</FormHelperText>
          )}
        </>
      )}
    />
  );
};